import { motion } from "framer-motion";
import { House, Buildings, Briefcase } from "@phosphor-icons/react";
import { useLang } from "../i18n/LangContext";
import { EditorialNumber } from "./EditorialNumber";

const icons = [House, Buildings, Briefcase];

const revealUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.09, duration: 0.9, ease: [0.7, 0, 0.2, 1] },
  }),
};

export const Audiences = () => {
  const { t } = useLang();
  const a = t.audiences;
  return (
    <section
      id="audiences"
      className="relative bg-[#F1EEE8] text-[#232326] py-28 md:py-36 overflow-hidden"
      data-testid="audiences-section"
    >
      {/* Ambient background — classic Italian villa with lake view, luxury contextual */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1773000857265-419482ddf6b2?auto=format&fit=crop&w=2400&q=90"
          alt=""
          className="w-full h-full object-cover object-center bg-ken-burns"
          style={{ filter: "contrast(0.98) brightness(1.02) saturate(0.95)" }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(241,238,232,0.74) 0%, rgba(241,238,232,0.66) 45%, rgba(241,238,232,0.80) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(241,238,232,0.0) 40%, rgba(35,35,38,0.14) 100%)",
          }}
        />
      </div>

      <EditorialNumber n="04" variant="dark" align="left" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={revealUp}
            className="md:col-span-3"
          >
            <div className="flex items-center gap-3 text-[#0F0E0C]/70 text-[0.7rem] tracking-[0.28em] uppercase font-medium">
              <span className="w-6 h-px bg-[#13535A]" />
              <span>{a.overline}</span>
            </div>
          </motion.div>
          <div className="md:col-span-9">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={1}
              className="font-display font-medium leading-[1.02] tracking-tight text-[clamp(2.2rem,5.4vw,4.6rem)] whitespace-pre-line mb-6 text-[#0F0E0C]"
            >
              {a.title}
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={revealUp}
              custom={2}
              className="text-[#0F0E0C]/85 max-w-2xl leading-relaxed text-[1rem] font-medium"
            >
              {a.lead}
            </motion.p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {a.items.map((it, i) => {
            const Icon = icons[i] || House;
            return (
              <motion.div
                key={it.k}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={revealUp}
                custom={i}
                className="group relative bg-[#E6E3DC] p-10 md:p-12 clip-card transition-colors duration-500 hover:bg-[#13535A] hover:text-[#F1EEE8] min-h-[360px] flex flex-col justify-between"
                data-testid={`audience-${it.k}`}
              >
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <span className="text-[0.66rem] tracking-[0.28em] uppercase text-[#8A6F45] group-hover:text-[#B08F5C] transition-colors font-medium">
                      {it.k} / 03
                    </span>
                    <Icon size={26} weight="light" className="text-[#13535A] group-hover:text-[#B08F5C] transition-colors" />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mb-6">
                    {it.title}
                    <span className="text-[#13535A] group-hover:text-[#B08F5C]">.</span>
                  </h3>
                </div>
                <p className="opacity-80 leading-relaxed text-[0.98rem]">{it.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
